import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"
import _imports_0 from '@/assets/icons/mini_tree_150.png'


const _withScopeId = n => (_pushScopeId("data-v-d86f61dc"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "family-group-scope" }
const _hoisted_2 = { class: "help-text" }
const _hoisted_3 = {
  key: 0,
  class: "step-actions"
}
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("button", {
  type: "button",
  class: "btn btn-primary",
  "data-bs-toggle": "modal",
  "data-bs-target": "#scopeModal"
}, " Select a family ", -1))
const _hoisted_5 = {
  key: 1,
  class: "scope-family"
}
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("img", {
  src: _imports_0,
  class: "scope-icon"
}, null, -1))
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", null, " Are you sure you want to skip this step? ", -1))

import { computed, nextTick, ref, watch } from 'vue'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { useFamilyGroupMemberStore } from '@/gp/FamilyGroupMemberStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { useTokenManager } from '@/auth/TokenManager'
import { LoadMode } from '@/util/AsyncData'
import { GroupMemberRole, ItemPermissions } from '@/gp/GroupAdminModel'
import { PatchChange } from '@/util/Api'
import { XIcon } from '@zhuowenli/vue-feather-icons'
import { isDefined } from '@/util/TypeScriptUtil'
import LoadingIndicator from '@/util/LoadingIndicator.vue'
import ViewFamilySelector from '@/manage/ViewFamilySelector.vue'
import FamilyGroupScopeModal from './FamilyGroupScopeModal.vue'
import Modal from '@/util/Modal.vue'
import TipAlert from '@/util/TipAlert.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'FamilyGroupScope',
  props: {
    familyGroupId: {},
    showStep: { type: Boolean },
    suppressEdit: { type: Boolean }
  },
  emits: [
  'loaded',
  'stepDismissed',
],
  setup(__props: any, { emit }) {

const props = __props;





const loadingRef = ref(false)
const permissionsRef = ref(ItemPermissions.None)
const candidateFamilyIdsRef = ref<string[]>([])

const familySelectorRef = ref<InstanceType<typeof ViewFamilySelector>>()
const confirmSkipModalRef = ref<InstanceType<typeof Modal>>()

const familyGroupStore = useFamilyGroupStore()
const familyGroupMemberStore = useFamilyGroupMemberStore()
const dataGroupStore = useDataGroupStore()
const viewPersonStore = useViewPersonStore()
const viewFamilyStore = useViewFamilyStore()
const tokenManager = useTokenManager()

const asyncFamilyGroupRef = computed(() => familyGroupStore.getAsyncGroup(props.familyGroupId, LoadMode.EnsureLoaded))

const familyGroupRef = computed(() => asyncFamilyGroupRef.value?.data)
const isOwnerRef = computed(() => (permissionsRef.value & ItemPermissions.Owner) > 0)
const showStepActionsRef = computed(() => props.showStep && candidateFamilyIdsRef.value.length > 0)
const enableEditRef = computed(() => isOwnerRef.value && !props.suppressEdit)
const emptyTextRef = computed(() => isOwnerRef.value && props.showStep ? 'Select a family' : undefined)
const showClearScopeRef = computed(() => !!familyGroupRef.value?.scopeFamilyId && enableEditRef.value)

watch(() => props.familyGroupId, async () => {
  permissionsRef.value = ItemPermissions.None
  loadingRef.value = true

  try {
    await asyncFamilyGroupRef.value?.whenLoadCompleted

    await viewFamilyStore.getFamilyAsync(familyGroupRef.value?.scopeFamilyId)

    const asyncPerms = familyGroupStore.getAsyncPermissions(props.familyGroupId, LoadMode.EnsureLoaded)
    permissionsRef.value = (await asyncPerms?.whenLoadCompleted) ?? ItemPermissions.None

    candidateFamilyIdsRef.value = []
    
    if (!familyGroupRef.value?.scopeFamilyId && isOwnerRef.value) {
      // find candidate scope families (parent families of this user who are not the scope family for any another family group owned by this user)
      const allGroupMembers = await familyGroupMemberStore.getMembersForUsersAsync([tokenManager.current.userId])
      const allOwnedGroupIds = allGroupMembers.filter(fm => fm.role == GroupMemberRole.Owner).map(fm => fm.familyGroupId)
      const allOwnedGroups = await familyGroupStore.getGroupListAsync(allOwnedGroupIds)
      const allScopeFamilyIds = new Set(allOwnedGroups.map(fg => fg.scopeFamilyId).filter(isDefined))

      const selfProfileGroup = await dataGroupStore.getAsyncGroup(tokenManager.current.userProfileId, LoadMode.EnsureLoaded)?.whenLoadCompleted
      if (selfProfileGroup?.startItemId) { // should always be true
        await viewPersonStore.ensureAncestorsLoadedAsync([selfProfileGroup.startItemId], 1)
        const parentFamilies = viewPersonStore.getLoadedParentFamilies(selfProfileGroup.startItemId).parentFamilies
        candidateFamilyIdsRef.value = parentFamilies.filter(vf => !vf.matchIds.some(id => allScopeFamilyIds.has(id))).map(vf => vf.id!)
      }
    }
  }
  finally {
    loadingRef.value = false
    emit('loaded')
  }
}, { immediate: true })

function showConfirmSkipModal() {
  confirmSkipModalRef.value?.open()
}

async function onSelectNone() {
  candidateFamilyIdsRef.value = []
  await nextTick()
  familySelectorRef.value?.showSearchInput()
}

async function onScopeChange(newFamilyId?: string) {
  if (newFamilyId != familyGroupRef.value?.scopeFamilyId) {
    const change = new PatchChange("/scopeFamilyId", newFamilyId)
    await familyGroupStore.patchAsync(props.familyGroupId!, [change])
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (loadingRef.value)
      ? (_openBlock(), _createBlock(LoadingIndicator, { key: 0 }))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("div", _hoisted_2, [
            (enableEditRef.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(" Select a set of common ancestors to help you choose group members. ")
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createTextVNode(" Group members should be related to this family. ")
                ], 64))
          ]),
          (showStepActionsRef.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _hoisted_4,
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-outline-primary btn-sm",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('stepDismissed')))
                }, " Skip ")
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _hoisted_6,
                _createVNode(ViewFamilySelector, {
                  ref_key: "familySelectorRef",
                  ref: familySelectorRef,
                  "model-value": familyGroupRef.value?.scopeFamilyId,
                  "text-only": "",
                  placeholder: "Type a name to search",
                  "can-edit": enableEditRef.value,
                  "empty-text": emptyTextRef.value,
                  "onUpdate:modelValue": onScopeChange
                }, null, 8, ["model-value", "can-edit", "empty-text"]),
                (showClearScopeRef.value)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      type: "button",
                      class: "btn btn-inline btn-link clear",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (onScopeChange()))
                    }, [
                      _createVNode(_unref(XIcon))
                    ]))
                  : _createCommentVNode("", true)
              ])),
          (_ctx.showStep && !showStepActionsRef.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 2,
                type: "button",
                class: "btn btn-inline btn-link",
                onClick: showConfirmSkipModal
              }, " Skip this step "))
            : _createCommentVNode("", true)
        ], 64)),
    _createVNode(FamilyGroupScopeModal, {
      id: "scopeModal",
      "family-group-id": _ctx.familyGroupId,
      "candidate-family-ids": candidateFamilyIdsRef.value,
      onSelectNone: onSelectNone
    }, null, 8, ["family-group-id", "candidate-family-ids"]),
    _createVNode(Modal, {
      ref_key: "confirmSkipModalRef",
      ref: confirmSkipModalRef,
      class: "confirm-skip-modal",
      "ok-close": "",
      onOk: _cache[2] || (_cache[2] = ($event: any) => (emit('stepDismissed')))
    }, {
      title: _withCtx(() => [
        _createTextVNode("Select common ancestors")
      ]),
      okText: _withCtx(() => [
        _createTextVNode("Skip this step")
      ]),
      default: _withCtx(() => [
        _createVNode(TipAlert, null, {
          default: _withCtx(() => [
            _createTextVNode(" Selecting common ancestors will let you choose group members from a list of their descendants. ")
          ]),
          _: 1
        }),
        _hoisted_7
      ]),
      _: 1
    }, 512)
  ]))
}
}

})